/**
 * staff-bus.js — WMM Staff Bus CRM view
 *
 * Renders the Staff Bus section under INTELLIGENCE.
 * Tabs: Queue | Post History | Intelligence (Phase 2 stub) | Run Log (Phase 2 stub)
 *
 * Pattern matches competition.js / business.js — template literals, apiFetch,
 * window-scoped handlers, DOM manipulation.
 */

import { apiFetch, runManualAction } from './utils.js';

// ─────────────────────────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'show-design',            label: 'Show Design' },
  { value: 'audio-sound-design',     label: 'Audio & Sound Design' },
  { value: 'composition-arranging',  label: 'Composition & Arranging' },
  { value: 'band-director-life',     label: 'Band Director Life' },
  { value: 'running-rehearsals',     label: 'Running Rehearsals' },
  { value: 'student-leadership',     label: 'Student Leadership' },
];

const CATEGORY_COLORS = {
  'show-design':           '#00b4d8',
  'audio-sound-design':    '#a855f7',
  'composition-arranging': '#22c55e',
  'band-director-life':    '#ff2d78',
  'running-rehearsals':    '#ffaa00',
  'student-leadership':    '#3b82f6',
};

// Active tab state
let _activeTab = 'queue';

// ─────────────────────────────────────────────────────────────────────────────
//  INIT
// ─────────────────────────────────────────────────────────────────────────────

export function initStaffBus() {
  window.renderStaffBusView = renderStaffBusView;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN RENDER
// ─────────────────────────────────────────────────────────────────────────────

export async function renderStaffBusView() {
  const container = document.getElementById('view-staff-bus');
  if (!container) return;

  container.innerHTML = `
    <div class="staff-bus-view">
      <div class="business-view-header" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:1.5rem;">
        <h2 style="margin:0;color:var(--text);font-size:1.25rem;font-weight:700;">Staff Bus</h2>
        <div style="display:flex;gap:8px;align-items:center;">
          <button onclick="window.sbRunPipeline()" class="sb-run-btn"
            style="background:var(--cyan);color:#0d1520;border:none;border-radius:6px;padding:8px 18px;font-weight:700;cursor:pointer;font-size:0.85rem;">
            ▶ Run Pipeline
          </button>
          <button onclick="window.sbRunIntel()" id="sb-run-intel-btn"
            style="background:var(--purple,#a855f7);color:#fff;border:none;border-radius:6px;padding:8px 14px;font-weight:700;cursor:pointer;font-size:0.85rem;">
            ▶ Run Intel
          </button>
          <span class="auto-inline-hint" id="ma-status-run_intel_pipeline-g" style="font-size:0.75rem;"></span>
          <button onclick="window.sbNewsletterPrompt()" id="sb-newsletter-prompt-btn"
            style="background:var(--border);color:var(--text);border:none;border-radius:6px;padding:8px 14px;font-weight:700;cursor:pointer;font-size:0.85rem;">
            ✉ Newsletter Prompt
          </button>
          <span class="auto-inline-hint" id="ma-status-prompt_newsletter_opener-g" style="font-size:0.75rem;"></span>
        </div>
      </div>

      <!-- Tabs -->
      <div class="sb-tabs" style="display:flex;gap:4px;border-bottom:1px solid var(--border);margin-bottom:1.5rem;">
        ${['queue','post-history','intelligence','run-log','editorial','newsletter'].map(t => `
          <button class="sb-tab${_activeTab === t ? ' sb-tab-active' : ''}"
            data-tab="${t}" onclick="window.sbSwitchTab('${t}')"
            style="padding:8px 16px;background:none;border:none;border-bottom:2px solid ${_activeTab === t ? 'var(--cyan)' : 'transparent'};
                   color:${_activeTab === t ? 'var(--cyan)' : 'var(--text-muted)'};cursor:pointer;font-size:0.875rem;font-weight:600;transition:all 0.15s;">
            ${t === 'queue' ? 'Queue' : t === 'post-history' ? 'Post History' : t === 'intelligence' ? 'Intelligence' : t === 'run-log' ? 'Run Log' : t === 'editorial' ? 'Editorial' : 'Newsletter'}
          </button>
        `).join('')}
      </div>

      <!-- Tab content -->
      <div id="sb-tab-content">
        <div class="loading-state" style="color:var(--text-muted);padding:2rem;">Loading…</div>
      </div>
    </div>
  `;

  await sbRenderTab(_activeTab);
}

// ─────────────────────────────────────────────────────────────────────────────
//  TAB SWITCHING
// ─────────────────────────────────────────────────────────────────────────────

window.sbSwitchTab = async function(tab) {
  _activeTab = tab;
  // Re-render full view to update tab active states
  await renderStaffBusView();
};

async function sbRenderTab(tab) {
  const content = document.getElementById('sb-tab-content');
  if (!content) return;

  if (tab === 'queue')        return sbRenderQueue(content);
  if (tab === 'post-history') return sbRenderHistory(content);
  if (tab === 'intelligence') return sbRenderIntelligence(content);
  if (tab === 'run-log')      return sbRenderRunLog(content);
  if (tab === 'editorial')    return sbRenderEditorial(content);
  if (tab === 'newsletter')   return sbRenderNewsletter(content);
}

// ─────────────────────────────────────────────────────────────────────────────
//  QUEUE TAB
// ─────────────────────────────────────────────────────────────────────────────

async function sbRenderQueue(container) {
  container.innerHTML = `<div class="loading-state" style="color:var(--text-muted);padding:2rem;">Loading queue…</div>`;
  try {
    const topics = await apiFetch('/api/blog/topics').catch(() => []);
    container.innerHTML = buildQueueHtml(topics);
  } catch (err) {
    container.innerHTML = `<div class="error-state" style="color:var(--red);padding:1rem;">Failed to load queue: ${err.message}</div>`;
  }
}

function buildQueueHtml(topics) {
  // Category balance bar
  const counts = {};
  CATEGORIES.forEach(c => { counts[c.value] = 0; });
  topics.forEach(t => { if (counts[t.category] !== undefined) counts[t.category]++; });

  const balanceBar = `
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:1.5rem;align-items:center;">
      <span style="font-size:0.75rem;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-right:4px;">Queue</span>
      ${CATEGORIES.map(c => `
        <span style="background:${CATEGORY_COLORS[c.value]}22;border:1px solid ${CATEGORY_COLORS[c.value]}55;
               color:${CATEGORY_COLORS[c.value]};border-radius:20px;padding:3px 10px;font-size:0.75rem;font-weight:600;">
          ${c.label.split(' ')[0]} · ${counts[c.value]}
        </span>
      `).join('')}
      <span style="margin-left:auto;font-size:0.75rem;color:var(--text-muted);">${topics.length} topic${topics.length !== 1 ? 's' : ''}</span>
    </div>
  `;

  if (topics.length === 0) {
    return balanceBar + `
      <div class="empty-state" style="text-align:center;padding:3rem;color:var(--text-muted);">
        <div style="font-size:2rem;margin-bottom:0.75rem;">✦</div>
        <div>Queue is empty — add a topic or wait for the intelligence pipeline.</div>
        <button onclick="window.sbOpenAddModal()"
          style="margin-top:1.25rem;background:var(--cyan);color:#0d1520;border:none;border-radius:6px;padding:9px 20px;font-weight:700;cursor:pointer;">
          + Add Topic
        </button>
      </div>
    `;
  }

  const rows = topics.map((t, i) => {
    const postByColor = getPostByColor(t.post_by);
    const postByText  = t.post_by ? formatPostBy(t.post_by) : '—';
    const catColor    = CATEGORY_COLORS[t.category] || 'var(--text-muted)';
    const catLabel    = CATEGORIES.find(c => c.value === t.category)?.label || t.category;
    const priorityColor = t.priority === 'High' ? 'var(--red)' : t.priority === 'Low' ? 'var(--text-muted)' : 'var(--amber)';

    return `
      <tr style="border-bottom:1px solid var(--border);">
        <td style="padding:10px 12px;font-size:0.8rem;color:var(--cyan);font-weight:700;white-space:nowrap;">#${t._score}</td>
        <td style="padding:10px 12px;max-width:320px;">
          <div style="color:var(--text);font-size:0.875rem;font-weight:600;line-height:1.4;">${escHtml(t.topic_angle)}</div>
          ${t.seo_title ? `<div style="color:var(--text-muted);font-size:0.75rem;margin-top:2px;">Title: ${escHtml(t.seo_title)}</div>` : ''}
          ${t.rationale ? `<div style="color:var(--text-muted);font-size:0.75rem;margin-top:2px;font-style:italic;">${escHtml(t.rationale.substring(0, 80))}${t.rationale.length > 80 ? '…' : ''}</div>` : ''}
        </td>
        <td style="padding:10px 12px;">
          <span style="background:${catColor}22;color:${catColor};border-radius:4px;padding:2px 8px;font-size:0.75rem;font-weight:600;white-space:nowrap;">
            ${catLabel}
          </span>
        </td>
        <td style="padding:10px 12px;">
          <span style="color:${priorityColor};font-size:0.8rem;font-weight:600;">${t.priority}</span>
        </td>
        <td style="padding:10px 12px;white-space:nowrap;">
          <span style="color:${postByColor};font-size:0.8rem;font-weight:600;">${postByText}</span>
        </td>
        <td style="padding:10px 12px;">
          <span style="color:var(--text-muted);font-size:0.75rem;">${t.source}</span>
        </td>
        <td style="padding:10px 12px;white-space:nowrap;">
          <button onclick="window.sbOpenEditModal(${t.id})"
            style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:4px;padding:3px 10px;font-size:0.75rem;cursor:pointer;margin-right:6px;">
            Edit
          </button>
          <button onclick="window.sbDeleteTopic(${t.id}, '${escHtml(t.topic_angle.substring(0, 30))}')"
            style="background:none;border:1px solid var(--border);color:var(--red);border-radius:4px;padding:3px 10px;font-size:0.75rem;cursor:pointer;">
            ✕
          </button>
        </td>
      </tr>
    `;
  }).join('');

  return `
    ${balanceBar}
    <div style="display:flex;justify-content:flex-end;margin-bottom:1rem;">
      <button onclick="window.sbOpenAddModal()"
        style="background:var(--cyan);color:#0d1520;border:none;border-radius:6px;padding:8px 18px;font-weight:700;cursor:pointer;font-size:0.85rem;">
        + Add Topic
      </button>
    </div>
    <div style="overflow-x:auto;border-radius:8px;border:1px solid var(--border);">
      <table style="width:100%;border-collapse:collapse;font-size:0.875rem;">
        <thead>
          <tr style="background:var(--bg-card);border-bottom:1px solid var(--border);">
            <th style="padding:10px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);white-space:nowrap;">Score</th>
            <th style="padding:10px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Topic</th>
            <th style="padding:10px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Category</th>
            <th style="padding:10px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Priority</th>
            <th style="padding:10px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Post By</th>
            <th style="padding:10px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Source</th>
            <th style="padding:10px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);"></th>
          </tr>
        </thead>
        <tbody style="background:var(--bg-card);">${rows}</tbody>
      </table>
    </div>
    ${buildTopicModal()}
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
//  POST HISTORY TAB
// ─────────────────────────────────────────────────────────────────────────────

async function sbRenderHistory(container) {
  container.innerHTML = `<div class="loading-state" style="color:var(--text-muted);padding:2rem;">Loading post history…</div>`;
  try {
    const posts = await apiFetch('/api/blog/history').catch(() => []);
    container.innerHTML = buildHistoryHtml(posts);
  } catch (err) {
    container.innerHTML = `<div class="error-state" style="color:var(--red);padding:1rem;">Failed to load history: ${err.message}</div>`;
  }
}

function buildHistoryHtml(posts) {
  if (posts.length === 0) {
    return `
      <div class="empty-state" style="text-align:center;padding:4rem;color:var(--text-muted);">
        <div style="font-size:2rem;margin-bottom:0.75rem;">◈</div>
        <div>No published posts yet. Run the pipeline to create the first draft.</div>
      </div>
    `;
  }

  const rows = posts.map(p => {
    const catColor = CATEGORY_COLORS[p.category] || 'var(--text-muted)';
    const catLabel = CATEGORIES.find(c => c.value === p.category)?.label || p.category || '—';
    const pubDate  = p.published_at ? new Date(p.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unpublished';
    const shopifyLink = p.shopify_id
      ? `<a href="https://${window._shopifyStore || 'whitemagemusic.myshopify.com'}/admin/articles/${p.shopify_id}"
           target="_blank" rel="noopener"
           style="color:var(--cyan);text-decoration:none;font-size:0.75rem;">Edit in Shopify →</a>`
      : '—';

    return `
      <tr style="border-bottom:1px solid var(--border);">
        <td style="padding:12px 14px;max-width:360px;">
          <div style="color:var(--text);font-weight:600;font-size:0.875rem;line-height:1.4;">${escHtml(p.title)}</div>
          ${p.topic_angle && p.topic_angle !== p.title ? `<div style="color:var(--text-muted);font-size:0.75rem;margin-top:2px;font-style:italic;">${escHtml(p.topic_angle.substring(0, 80))}${p.topic_angle.length > 80 ? '…' : ''}</div>` : ''}
        </td>
        <td style="padding:12px 14px;">
          <span style="background:${catColor}22;color:${catColor};border-radius:4px;padding:2px 8px;font-size:0.75rem;font-weight:600;white-space:nowrap;">
            ${catLabel}
          </span>
        </td>
        <td style="padding:12px 14px;color:var(--text-muted);font-size:0.8rem;white-space:nowrap;">${pubDate}</td>
        <td style="padding:12px 14px;">${shopifyLink}</td>
      </tr>
    `;
  }).join('');

  return `
    <div style="overflow-x:auto;border-radius:8px;border:1px solid var(--border);">
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr style="background:var(--bg-card);border-bottom:1px solid var(--border);">
            <th style="padding:10px 14px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Post</th>
            <th style="padding:10px 14px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Category</th>
            <th style="padding:10px 14px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Published</th>
            <th style="padding:10px 14px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Shopify</th>
          </tr>
        </thead>
        <tbody style="background:var(--bg-card);">${rows}</tbody>
      </table>
    </div>
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
//  ADD / EDIT MODAL
// ─────────────────────────────────────────────────────────────────────────────

function buildTopicModal() {
  const categoryOptions = CATEGORIES.map(c =>
    `<option value="${c.value}">${c.label}</option>`
  ).join('');

  return `
    <div id="sb-topic-modal" class="modal-overlay hidden">
      <div class="modal-dialog" style="max-width:560px;width:100%;">
        <div class="modal-header">
          <span id="sb-modal-title">Add Topic</span>
          <button class="modal-close-btn" onclick="window.sbCloseModal()">×</button>
        </div>
        <div class="modal-body" style="display:grid;gap:12px;">
          <input type="hidden" id="sb-topic-id" value="">

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Topic Angle <span style="color:var(--red);">*</span></label>
            <textarea id="sb-topic-angle" rows="3" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;resize:vertical;"
              placeholder="The specific angle or hook for this post…"></textarea>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div class="form-field">
              <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Category <span style="color:var(--red);">*</span></label>
              <select id="sb-category" class="form-input"
                style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;">
                ${categoryOptions}
              </select>
            </div>
            <div class="form-field">
              <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Priority</label>
              <select id="sb-priority" class="form-input"
                style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;">
                <option value="High">High</option>
                <option value="Medium" selected>Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">SEO Title Override</label>
            <input type="text" id="sb-seo-title" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;"
              placeholder="Leave blank to let Claude write the title">
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Keywords (comma-separated)</label>
            <input type="text" id="sb-keywords" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;"
              placeholder="marching band show design, UIL, …">
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div class="form-field">
              <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Post By</label>
              <input type="date" id="sb-post-by" class="form-input"
                style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;">
            </div>
            <div class="form-field">
              <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Horizon</label>
              <input type="text" id="sb-horizon" class="form-input"
                style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;"
                placeholder="4-6 weeks, current, evergreen…">
            </div>
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Rationale</label>
            <textarea id="sb-rationale" rows="2" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;resize:vertical;"
              placeholder="Why this topic, why now…"></textarea>
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Director Pain Point</label>
            <textarea id="sb-director-pain" rows="2" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;resize:vertical;"
              placeholder="The specific problem this post addresses…"></textarea>
          </div>
        </div>
        <div class="modal-footer" style="display:flex;justify-content:flex-end;gap:10px;margin-top:16px;">
          <button onclick="window.sbCloseModal()"
            style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:6px;padding:8px 18px;cursor:pointer;">
            Cancel
          </button>
          <button onclick="window.sbSaveTopic()"
            style="background:var(--cyan);color:#0d1520;border:none;border-radius:6px;padding:8px 20px;font-weight:700;cursor:pointer;">
            Save Topic
          </button>
        </div>
      </div>
    </div>
  `;
}

window.sbOpenAddModal = function() {
  const modal = document.getElementById('sb-topic-modal');
  if (!modal) return;
  document.getElementById('sb-modal-title').textContent = 'Add Topic';
  document.getElementById('sb-topic-id').value        = '';
  document.getElementById('sb-topic-angle').value     = '';
  document.getElementById('sb-category').value        = 'show-design';
  document.getElementById('sb-priority').value        = 'Medium';
  document.getElementById('sb-seo-title').value       = '';
  document.getElementById('sb-keywords').value        = '';
  document.getElementById('sb-post-by').value         = '';
  document.getElementById('sb-horizon').value         = '';
  document.getElementById('sb-rationale').value       = '';
  document.getElementById('sb-director-pain').value   = '';
  modal.classList.remove('hidden');
};

window.sbOpenEditModal = async function(id) {
  const modal = document.getElementById('sb-topic-modal');
  if (!modal) return;
  try {
    const topics = await apiFetch('/api/blog/topics');
    const t = topics.find(t => t.id === id);
    if (!t) return alert('Topic not found');

    document.getElementById('sb-modal-title').textContent  = 'Edit Topic';
    document.getElementById('sb-topic-id').value           = t.id;
    document.getElementById('sb-topic-angle').value        = t.topic_angle || '';
    document.getElementById('sb-category').value           = t.category || 'show-design';
    document.getElementById('sb-priority').value           = t.priority || 'Medium';
    document.getElementById('sb-seo-title').value          = t.seo_title || '';
    document.getElementById('sb-keywords').value           = t.keywords || '';
    document.getElementById('sb-post-by').value            = t.post_by ? t.post_by.split('T')[0] : '';
    document.getElementById('sb-horizon').value            = t.forward_horizon || '';
    document.getElementById('sb-rationale').value          = t.rationale || '';
    document.getElementById('sb-director-pain').value      = t.director_pain || '';
    modal.classList.remove('hidden');
  } catch (err) {
    alert('Failed to load topic: ' + err.message);
  }
};

window.sbCloseModal = function() {
  document.getElementById('sb-topic-modal')?.classList.add('hidden');
};

window.sbSaveTopic = async function() {
  const id    = document.getElementById('sb-topic-id').value;
  const body  = {
    topic_angle:     document.getElementById('sb-topic-angle').value.trim(),
    category:        document.getElementById('sb-category').value,
    priority:        document.getElementById('sb-priority').value,
    seo_title:       document.getElementById('sb-seo-title').value.trim() || null,
    keywords:        document.getElementById('sb-keywords').value.trim() || null,
    post_by:         document.getElementById('sb-post-by').value || null,
    forward_horizon: document.getElementById('sb-horizon').value.trim() || null,
    rationale:       document.getElementById('sb-rationale').value.trim() || null,
    director_pain:   document.getElementById('sb-director-pain').value.trim() || null,
  };

  if (!body.topic_angle) return alert('Topic angle is required');

  try {
    if (id) {
      await apiFetch(`/api/blog/topics/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      await apiFetch('/api/blog/topics', { method: 'POST', body: JSON.stringify(body) });
    }
    window.sbCloseModal();
    await renderStaffBusView();
  } catch (err) {
    alert('Failed to save: ' + err.message);
  }
};

window.sbDeleteTopic = async function(id, preview) {
  if (!confirm(`Delete topic: "${preview}…"?\n\nThis cannot be undone.`)) return;
  try {
    await apiFetch(`/api/blog/topics/${id}`, { method: 'DELETE' });
    await renderStaffBusView();
  } catch (err) {
    alert('Failed to delete: ' + err.message);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  INTELLIGENCE TAB
// ─────────────────────────────────────────────────────────────────────────────

async function sbRenderIntelligence(container) {
  container.innerHTML = `<div class="loading-state" style="color:var(--text-muted);padding:2rem;">Loading intelligence data…</div>`;
  try {
    const [sources, smes] = await Promise.all([
      apiFetch('/api/blog/intel/sources').catch(() => []),
      apiFetch('/api/blog/intel/smes').catch(() => []),
    ]);
    container.innerHTML = buildIntelligenceHtml(sources, smes);
  } catch (err) {
    container.innerHTML = `<div class="error-state" style="color:var(--red);padding:1rem;">Failed to load: ${err.message}</div>`;
  }
}

function buildIntelligenceHtml(sources, smes) {
  const tier1 = sources.filter(s => s.tier === 1);
  const tier2 = sources.filter(s => s.tier === 2);

  function sourceRow(s) {
    const lastScraped = s.last_scraped
      ? relativeTime(new Date(s.last_scraped))
      : 'Never';
    const signalCount = s.last_signal_count != null ? s.last_signal_count : '—';
    return `
      <tr style="border-bottom:1px solid var(--border);">
        <td style="padding:10px 12px;">
          <div style="color:var(--text);font-size:0.875rem;font-weight:600;">${escHtml(s.name)}</div>
          <div style="color:var(--text-muted);font-size:0.72rem;margin-top:2px;">${escHtml(s.type)}</div>
        </td>
        <td style="padding:10px 12px;color:var(--text-muted);font-size:0.8rem;">${lastScraped}</td>
        <td style="padding:10px 12px;text-align:center;">
          <span style="color:var(--cyan);font-size:0.875rem;font-weight:700;">${signalCount}</span>
        </td>
        <td style="padding:10px 12px;text-align:center;">
          <button onclick="window.sbToggleSource('${escHtml(s.id)}', ${!s.active})"
            style="background:${s.active ? 'var(--green)22' : 'var(--border)'};
                   border:1px solid ${s.active ? 'var(--green)' : 'var(--border)'};
                   color:${s.active ? 'var(--green)' : 'var(--text-muted)'};
                   border-radius:20px;padding:3px 12px;font-size:0.75rem;font-weight:600;cursor:pointer;">
            ${s.active ? 'Active' : 'Paused'}
          </button>
        </td>
      </tr>
    `;
  }

  function sourcesPanel(label, rows) {
    return `
      <div style="margin-bottom:1.5rem;">
        <div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;
                    color:var(--text-muted);margin-bottom:0.75rem;">${label}</div>
        <div style="border-radius:8px;border:1px solid var(--border);overflow:hidden;">
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="background:var(--bg-card);border-bottom:1px solid var(--border);">
                <th style="padding:8px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Source</th>
                <th style="padding:8px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Last Run</th>
                <th style="padding:8px 12px;text-align:center;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Signals</th>
                <th style="padding:8px 12px;text-align:center;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Status</th>
              </tr>
            </thead>
            <tbody style="background:var(--bg-card);">${rows.map(sourceRow).join('')}</tbody>
          </table>
        </div>
      </div>
    `;
  }

  function smeCard(sme) {
    const keywords = Array.isArray(sme.activation_keywords) ? sme.activation_keywords : [];
    return `
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:8px;padding:14px 16px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
          <div>
            <div style="color:var(--text);font-weight:700;font-size:0.875rem;">${escHtml(sme.name)}</div>
            <div style="color:var(--text-muted);font-size:0.75rem;margin-top:2px;">${escHtml(sme.title || '')}</div>
          </div>
          <div style="display:flex;gap:6px;align-items:center;">
            <span style="font-size:0.7rem;color:${sme.active ? 'var(--green)' : 'var(--text-muted)'};">
              ${sme.active ? '● Active' : '○ Inactive'}
            </span>
            <button onclick="window.sbOpenSmeModal('${escHtml(sme.id)}')"
              style="background:none;border:1px solid var(--border);color:var(--text-muted);
                     border-radius:4px;padding:2px 8px;font-size:0.72rem;cursor:pointer;">Edit</button>
            <button onclick="window.sbDeleteSme('${escHtml(sme.id)}', '${escHtml(sme.name)}')"
              style="background:none;border:1px solid var(--border);color:var(--red);
                     border-radius:4px;padding:2px 8px;font-size:0.72rem;cursor:pointer;">✕</button>
          </div>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:4px;">
          ${keywords.slice(0, 8).map(k =>
            `<span style="background:var(--border);color:var(--text-muted);border-radius:3px;padding:1px 6px;font-size:0.7rem;">${escHtml(k)}</span>`
          ).join('')}
          ${keywords.length > 8 ? `<span style="color:var(--text-muted);font-size:0.7rem;">+${keywords.length - 8} more</span>` : ''}
        </div>
      </div>
    `;
  }

  return `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:2rem;">

      <!-- Sources panel -->
      <div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;">
          <h3 style="margin:0;font-size:1rem;color:var(--text);">Intel Sources</h3>
          <button onclick="window.sbRunIntel()"
            style="background:none;border:1px solid var(--cyan);color:var(--cyan);
                   border-radius:6px;padding:5px 14px;font-size:0.8rem;font-weight:600;cursor:pointer;">
            ▶ Run Intel
          </button>
        </div>
        ${sourcesPanel('Tier 1 — Director Forums', tier1)}
        ${sourcesPanel('Tier 2 — Industry Publications', tier2)}
      </div>

      <!-- SME panel -->
      <div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;">
          <h3 style="margin:0;font-size:1rem;color:var(--text);">SME Advisory Council</h3>
          <button onclick="window.sbOpenSmeModal(null)"
            style="background:var(--cyan);color:#0d1520;border:none;
                   border-radius:6px;padding:5px 14px;font-size:0.8rem;font-weight:700;cursor:pointer;">
            + Add SME
          </button>
        </div>
        <div style="display:grid;gap:10px;">
          ${smes.map(smeCard).join('')}
          ${smes.length === 0 ? '<div style="color:var(--text-muted);font-size:0.875rem;">No SME agents found.</div>' : ''}
        </div>
      </div>

    </div>
    ${buildSmeModal()}
  `;
}

// Source toggle
window.sbToggleSource = async function(id, newActive) {
  try {
    await apiFetch(`/api/blog/intel/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active: newActive }),
    });
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderIntelligence(content);
  } catch (err) {
    alert('Failed to update source: ' + err.message);
  }
};

// Intel pipeline manual trigger from Intelligence tab
window.sbRunIntel = async function() {
  if (!confirm('Run the intelligence pipeline now?\n\nThis will scrape all active sources, activate SMEs, synthesize signals, and write new topics to the queue.')) return;
  try {
    await apiFetch('/api/blog/intel/run', { method: 'POST' });
    alert('Intelligence pipeline started. Check the Run Log tab for progress.');
  } catch (err) {
    alert('Failed to start: ' + err.message);
  }
};

// ── SME Modal ──────────────────────────────────────────────────────────────────

function buildSmeModal() {
  return `
    <div id="sb-sme-modal" class="modal-overlay hidden">
      <div class="modal-dialog" style="max-width:600px;width:100%;">
        <div class="modal-header">
          <span id="sb-sme-modal-title">Add SME Agent</span>
          <button class="modal-close-btn" onclick="window.sbCloseSmeModal()">×</button>
        </div>
        <div class="modal-body" style="display:grid;gap:12px;">
          <input type="hidden" id="sb-sme-id-field" value="">

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div class="form-field">
              <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">ID (slug) <span style="color:var(--red);">*</span></label>
              <input type="text" id="sb-sme-id" class="form-input"
                style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;"
                placeholder="e.g. my_expert">
            </div>
            <div class="form-field">
              <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Display Name <span style="color:var(--red);">*</span></label>
              <input type="text" id="sb-sme-name" class="form-input"
                style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;"
                placeholder="e.g. Dr. Music Ed">
            </div>
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Title / Role</label>
            <input type="text" id="sb-sme-title" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;"
              placeholder="e.g. Music Education Research Scholar">
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Activation Keywords (comma-separated)</label>
            <input type="text" id="sb-sme-keywords" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;"
              placeholder="rehearsal, pedagogy, assessment, …">
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Lens Prompt <span style="color:var(--red);">*</span></label>
            <textarea id="sb-sme-lens" rows="8" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.8rem;resize:vertical;font-family:monospace;"
              placeholder="You specialize in… You do NOT write blog posts. You provide 2-3 insight bullets…"></textarea>
          </div>

          <div class="form-field">
            <label style="font-size:0.75rem;color:var(--text-muted);display:block;margin-bottom:4px;">Retrain Notes</label>
            <textarea id="sb-sme-notes" rows="2" class="form-input"
              style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;padding:8px 10px;font-size:0.875rem;resize:vertical;"
              placeholder="Notes from Ryan on how to retrain this agent…"></textarea>
          </div>
        </div>
        <div class="modal-footer" style="display:flex;justify-content:flex-end;gap:10px;margin-top:16px;">
          <button onclick="window.sbCloseSmeModal()"
            style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:6px;padding:8px 18px;cursor:pointer;">
            Cancel
          </button>
          <button onclick="window.sbSaveSme()"
            style="background:var(--cyan);color:#0d1520;border:none;border-radius:6px;padding:8px 20px;font-weight:700;cursor:pointer;">
            Save SME
          </button>
        </div>
      </div>
    </div>
  `;
}

window.sbOpenSmeModal = async function(id) {
  const modal = document.getElementById('sb-sme-modal');
  if (!modal) return;

  if (id) {
    // Edit mode — fetch current data
    try {
      const smes = await apiFetch('/api/blog/intel/smes');
      const sme  = smes.find(s => s.id === id);
      if (!sme) return alert('SME not found');

      document.getElementById('sb-sme-modal-title').textContent = 'Edit SME Agent';
      document.getElementById('sb-sme-id-field').value  = sme.id;
      document.getElementById('sb-sme-id').value        = sme.id;
      document.getElementById('sb-sme-id').disabled     = true; // can't rename slug
      document.getElementById('sb-sme-name').value      = sme.name || '';
      document.getElementById('sb-sme-title').value     = sme.title || '';
      document.getElementById('sb-sme-keywords').value  = Array.isArray(sme.activation_keywords) ? sme.activation_keywords.join(', ') : '';
      document.getElementById('sb-sme-lens').value      = sme.lens_prompt || '';
      document.getElementById('sb-sme-notes').value     = sme.retrain_notes || '';
    } catch (err) {
      return alert('Failed to load SME: ' + err.message);
    }
  } else {
    // Add mode
    document.getElementById('sb-sme-modal-title').textContent = 'Add SME Agent';
    document.getElementById('sb-sme-id-field').value  = '';
    document.getElementById('sb-sme-id').value        = '';
    document.getElementById('sb-sme-id').disabled     = false;
    document.getElementById('sb-sme-name').value      = '';
    document.getElementById('sb-sme-title').value     = '';
    document.getElementById('sb-sme-keywords').value  = '';
    document.getElementById('sb-sme-lens').value      = '';
    document.getElementById('sb-sme-notes').value     = '';
  }

  modal.classList.remove('hidden');
};

window.sbCloseSmeModal = function() {
  document.getElementById('sb-sme-modal')?.classList.add('hidden');
};

window.sbSaveSme = async function() {
  const existingId = document.getElementById('sb-sme-id-field').value;
  const id         = document.getElementById('sb-sme-id').value.trim();
  const name       = document.getElementById('sb-sme-name').value.trim();
  const lens       = document.getElementById('sb-sme-lens').value.trim();

  if (!name) return alert('Display name is required');
  if (!lens) return alert('Lens prompt is required');
  if (!existingId && !id) return alert('ID slug is required');

  const body = {
    name,
    title:               document.getElementById('sb-sme-title').value.trim() || null,
    activation_keywords: document.getElementById('sb-sme-keywords').value.trim() || null,
    lens_prompt:         lens,
    retrain_notes:       document.getElementById('sb-sme-notes').value.trim() || null,
  };

  try {
    if (existingId) {
      await apiFetch(`/api/blog/intel/smes/${existingId}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      await apiFetch('/api/blog/intel/smes', { method: 'POST', body: JSON.stringify({ ...body, id }) });
    }
    window.sbCloseSmeModal();
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderIntelligence(content);
  } catch (err) {
    alert('Failed to save SME: ' + err.message);
  }
};

window.sbDeleteSme = async function(id, name) {
  if (!confirm(`Delete SME agent "${name}"?\n\nThis cannot be undone.`)) return;
  try {
    await apiFetch(`/api/blog/intel/smes/${id}`, { method: 'DELETE' });
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderIntelligence(content);
  } catch (err) {
    alert('Failed to delete: ' + err.message);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  RUN LOG TAB
// ─────────────────────────────────────────────────────────────────────────────

async function sbRenderRunLog(container) {
  container.innerHTML = `<div class="loading-state" style="color:var(--text-muted);padding:2rem;">Loading run log…</div>`;
  try {
    const [blogLog, intelLog] = await Promise.all([
      apiFetch('/api/blog/status').catch(() => []),
      apiFetch('/api/blog/intel/log').catch(() => []),
    ]);
    container.innerHTML = buildRunLogHtml(blogLog, intelLog);
  } catch (err) {
    container.innerHTML = `<div class="error-state" style="color:var(--red);padding:1rem;">Failed to load log: ${err.message}</div>`;
  }
}

let _logFilter = 'all';

function buildRunLogHtml(blogLog, intelLog) {
  const all = [...blogLog, ...intelLog]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 60);

  const filtered = _logFilter === 'all' ? all
    : all.filter(r => r.run_type === (_logFilter === 'blog' ? 'blog_pipeline' : 'intel_pipeline'));

  function eventColor(event) {
    if (!event) return 'var(--text-muted)';
    if (event.includes('ERROR') || event.includes('FAILED')) return 'var(--red)';
    if (event.includes('COMPLETE') || event.includes('WRITTEN') || event.includes('SENT')) return 'var(--green)';
    if (event.includes('START') || event.includes('TRIGGER')) return 'var(--cyan)';
    return 'var(--text-muted)';
  }

  const rows = filtered.map(r => {
    const ts = new Date(r.created_at).toLocaleString('en-US', {
      timeZone: 'America/Chicago', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
    const pipelineLabel = r.run_type === 'blog_pipeline' ? 'Blog' : 'Intel';
    const pipelineColor = r.run_type === 'blog_pipeline' ? 'var(--cyan)' : '#a855f7';
    return `
      <tr style="border-bottom:1px solid var(--border);">
        <td style="padding:8px 12px;white-space:nowrap;color:var(--text-muted);font-size:0.75rem;">${ts}</td>
        <td style="padding:8px 12px;">
          <span style="background:${pipelineColor}22;color:${pipelineColor};border-radius:3px;padding:1px 7px;font-size:0.7rem;font-weight:600;">${pipelineLabel}</span>
        </td>
        <td style="padding:8px 12px;">
          <span style="color:${eventColor(r.event)};font-size:0.78rem;font-weight:600;">${escHtml(r.event || '')}</span>
        </td>
        <td style="padding:8px 12px;color:var(--text-muted);font-size:0.78rem;max-width:380px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          ${escHtml(r.message || '')}
        </td>
      </tr>
    `;
  }).join('');

  const filterBtn = (val, label) => `
    <button onclick="window.sbSetLogFilter('${val}')"
      style="background:${_logFilter === val ? 'var(--cyan)' : 'none'};
             color:${_logFilter === val ? '#0d1520' : 'var(--text-muted)'};
             border:1px solid ${_logFilter === val ? 'var(--cyan)' : 'var(--border)'};
             border-radius:5px;padding:4px 12px;font-size:0.78rem;font-weight:600;cursor:pointer;">
      ${label}
    </button>
  `;

  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;">
      <div style="display:flex;gap:6px;">
        ${filterBtn('all', 'All')}
        ${filterBtn('blog', 'Blog Pipeline')}
        ${filterBtn('intel', 'Intel Pipeline')}
      </div>
      <div style="display:flex;gap:8px;">
        <button onclick="window.sbOpenSchedule()"
          style="background:none;border:1px solid var(--cyan);color:var(--cyan);
                 border-radius:6px;padding:5px 14px;font-size:0.78rem;font-weight:600;cursor:pointer;">
          ⏱ Schedule
        </button>
        <button onclick="window.sbRefreshLog()"
          style="background:none;border:1px solid var(--border);color:var(--text-muted);
                 border-radius:6px;padding:5px 14px;font-size:0.78rem;cursor:pointer;">
          ↺ Refresh
        </button>
      </div>
    </div>
    ${filtered.length === 0
      ? `<div style="text-align:center;padding:3rem;color:var(--text-muted);">No log entries yet.</div>`
      : `<div style="overflow-x:auto;border-radius:8px;border:1px solid var(--border);">
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="background:var(--bg-card);border-bottom:1px solid var(--border);">
                <th style="padding:8px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);white-space:nowrap;">Time (CT)</th>
                <th style="padding:8px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Pipeline</th>
                <th style="padding:8px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Event</th>
                <th style="padding:8px 12px;text-align:left;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.5px;color:var(--text-muted);">Message</th>
              </tr>
            </thead>
            <tbody style="background:var(--bg-card);">${rows}</tbody>
          </table>
        </div>`
    }
  `;
}

// ─────────────────────────────────────────────────────────────────────────────
//  SCHEDULE MODAL
// ─────────────────────────────────────────────────────────────────────────────

window.sbOpenSchedule = async function() {
  let jobs;
  try {
    jobs = await apiFetch('/api/blog/schedule');
  } catch (err) {
    alert('Failed to load schedules: ' + err.message);
    return;
  }

  // Remove existing modal if present
  document.getElementById('sb-schedule-modal')?.remove();

  const rows = jobs.map(job => `
    <tr style="border-bottom:1px solid var(--border);" id="sched-row-${job.key}">
      <td style="padding:12px 14px;">
        <div style="font-size:0.875rem;font-weight:600;color:var(--text);">${escHtml(job.label)}</div>
        <div style="font-size:0.75rem;color:var(--text-muted);margin-top:2px;">${escHtml(job.description)}</div>
      </td>
      <td style="padding:12px 14px;white-space:nowrap;">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
          <input type="checkbox" id="sched-enabled-${job.key}" ${job.enabled ? 'checked' : ''}
            onchange="window.sbSchedulePatch('${job.key}', {enabled: this.checked})"
            style="width:16px;height:16px;accent-color:var(--cyan);cursor:pointer;">
          <span style="font-size:0.8rem;color:${job.enabled ? 'var(--cyan)' : 'var(--text-muted)'};">
            ${job.enabled ? 'Active' : 'Paused'}
          </span>
        </label>
      </td>
      <td style="padding:12px 14px;">
        <div style="display:flex;align-items:center;gap:8px;">
          <input type="text" id="sched-expr-${job.key}" value="${escHtml(job.schedule)}"
            style="background:var(--bg);border:1px solid var(--border);color:var(--text);
                   border-radius:5px;padding:4px 10px;font-size:0.8rem;font-family:monospace;width:140px;">
          <button onclick="window.sbScheduleSave('${job.key}')"
            style="background:var(--cyan);color:#0d1520;border:none;border-radius:5px;
                   padding:4px 12px;font-size:0.78rem;font-weight:700;cursor:pointer;">
            Save
          </button>
        </div>
        <div style="font-size:0.7rem;color:var(--text-muted);margin-top:4px;" id="sched-hint-${job.key}">
          ${cronHint(job.schedule)}
        </div>
      </td>
    </tr>
  `).join('');

  const modal = document.createElement('div');
  modal.id = 'sb-schedule-modal';
  modal.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:9999;
    display:flex;align-items:center;justify-content:center;padding:20px;`;
  modal.innerHTML = `
    <div style="background:var(--card);border:1px solid var(--border);border-radius:12px;
                width:100%;max-width:760px;max-height:90vh;overflow-y:auto;">
      <div style="padding:20px 24px;border-bottom:1px solid var(--border);
                  display:flex;justify-content:space-between;align-items:center;">
        <h3 style="margin:0;font-size:1rem;color:var(--text);">Pipeline Schedule</h3>
        <button onclick="document.getElementById('sb-schedule-modal').remove()"
          style="background:none;border:none;color:var(--text-muted);font-size:1.25rem;cursor:pointer;">✕</button>
      </div>
      <div style="padding:8px 0;">
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="border-bottom:1px solid var(--border);">
              <th style="padding:8px 14px;text-align:left;font-size:0.7rem;text-transform:uppercase;
                         letter-spacing:0.5px;color:var(--text-muted);">Pipeline</th>
              <th style="padding:8px 14px;text-align:left;font-size:0.7rem;text-transform:uppercase;
                         letter-spacing:0.5px;color:var(--text-muted);">Status</th>
              <th style="padding:8px 14px;text-align:left;font-size:0.7rem;text-transform:uppercase;
                         letter-spacing:0.5px;color:var(--text-muted);">Cron Expression</th>
            </tr>
          </thead>
          <tbody style="background:var(--card);">${rows}</tbody>
        </table>
      </div>
      <div style="padding:14px 24px;border-top:1px solid var(--border);">
        <p style="margin:0;font-size:0.75rem;color:var(--text-muted);">
          Cron format: <code style="color:var(--cyan);">minute hour day month weekday</code>
          &nbsp;·&nbsp; All times Central (America/Chicago)
          &nbsp;·&nbsp; <code style="color:var(--cyan);">1,4</code> = Mon &amp; Thu
          &nbsp;·&nbsp; <code style="color:var(--cyan);">0</code> = Sun
        </p>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
};

function cronHint(expr) {
  if (!expr) return '';
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return 'custom expression';
  const [min, hr, , , dow] = parts;
  const days = { '0':'Sun','1':'Mon','2':'Tue','3':'Wed','4':'Thu','5':'Fri','6':'Sat' };
  const dayStr = dow === '*' ? 'every day'
    : dow.split(',').map(d => days[d] || d).join(' & ');
  const hrNum = parseInt(hr, 10);
  if (!isNaN(hrNum)) {
    const ampm = hrNum < 12 ? 'AM' : 'PM';
    const h12 = hrNum % 12 || 12;
    const minStr = min === '0' ? '' : `:${min.padStart(2,'0')}`;
    return `${dayStr} at ${h12}${minStr} ${ampm} CT`;
  }
  return expr;
}

window.sbSchedulePatch = async function(key, patch) {
  try {
    await apiFetch(`/api/blog/schedule/${key}`, { method: 'PATCH', body: JSON.stringify(patch) });
    // Update hint text color for enabled state
    const label = document.querySelector(`#sched-row-${key} span`);
    if (label && patch.enabled !== undefined) {
      label.textContent = patch.enabled ? 'Active' : 'Paused';
      label.style.color = patch.enabled ? 'var(--cyan)' : 'var(--text-muted)';
    }
  } catch (err) {
    alert(`Failed to update schedule: ${err.message}`);
    // Revert checkbox
    const cb = document.getElementById(`sched-enabled-${key}`);
    if (cb && patch.enabled !== undefined) cb.checked = !patch.enabled;
  }
};

window.sbScheduleSave = async function(key) {
  const input = document.getElementById(`sched-expr-${key}`);
  if (!input) return;
  try {
    const result = await apiFetch(`/api/blog/schedule/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ schedule: input.value.trim() }),
    });
    const hint = document.getElementById(`sched-hint-${key}`);
    if (hint) hint.textContent = cronHint(result.schedule);
    input.style.borderColor = 'var(--cyan)';
    setTimeout(() => { input.style.borderColor = 'var(--border)'; }, 1500);
  } catch (err) {
    alert(`Invalid schedule: ${err.message}`);
    input.style.borderColor = 'var(--red)';
    setTimeout(() => { input.style.borderColor = 'var(--border)'; }, 2000);
  }
};

window.sbSetLogFilter = async function(val) {
  _logFilter = val;
  const content = document.getElementById('sb-tab-content');
  if (content) await sbRenderRunLog(content);
};

window.sbRefreshLog = async function() {
  const content = document.getElementById('sb-tab-content');
  if (content) await sbRenderRunLog(content);
};

// ─────────────────────────────────────────────────────────────────────────────
//  PIPELINE RUN BUTTON
// ─────────────────────────────────────────────────────────────────────────────

window.sbRunPipeline = async function() {
  if (!confirm('Run the blog pipeline now?\n\nThis will select the top-scored topic, generate a post and image, create a Shopify draft, and send a preview email.')) return;
  try {
    const btn = document.querySelector('.sb-run-btn');
    if (btn) { btn.textContent = '⏳ Running…'; btn.disabled = true; }
    await apiFetch('/api/blog/run', { method: 'POST' });
    alert('Pipeline started! Check your inbox for the preview email. Run log will update shortly.');
  } catch (err) {
    alert('Failed to start pipeline: ' + err.message);
  } finally {
    const btn = document.querySelector('.sb-run-btn');
    if (btn) { btn.textContent = '▶ Run Pipeline'; btn.disabled = false; }
  }
};

window.sbRunIntel = async function() {
  const btn = document.getElementById('sb-run-intel-btn');
  if (btn) { btn.textContent = '⏳ Running…'; btn.disabled = true; }
  try {
    await runManualAction('run_intel_pipeline', {}, 'ma-status-run_intel_pipeline-g');
  } finally {
    if (btn) { btn.textContent = '▶ Run Intel'; btn.disabled = false; }
  }
};

window.sbNewsletterPrompt = async function() {
  const btn = document.getElementById('sb-newsletter-prompt-btn');
  if (btn) { btn.textContent = '⏳ Sending…'; btn.disabled = true; }
  try {
    await runManualAction('prompt_newsletter_opener', {}, 'ma-status-prompt_newsletter_opener-g');
  } finally {
    if (btn) { btn.textContent = '✉ Newsletter Prompt'; btn.disabled = false; }
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function getPostByColor(postBy) {
  if (!postBy) return 'var(--text-muted)';
  const days = (new Date(postBy) - new Date()) / 86400000;
  if (days < 0)  return '#ff4757';  // overdue — red
  if (days < 7)  return '#ffaa00';  // amber
  if (days < 14) return '#e8c547';  // yellow
  return 'var(--text-muted)';
}

function formatPostBy(postBy) {
  const days = Math.round((new Date(postBy) - new Date()) / 86400000);
  const dateStr = new Date(postBy).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days < 0)  return `${dateStr} (overdue)`;
  if (days === 0) return `${dateStr} (today)`;
  if (days < 7)  return `${dateStr} (${days}d)`;
  return dateStr;
}

function relativeTime(date) {
  if (!date || isNaN(date)) return 'Never';
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60)   return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)   return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)     return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30)      return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function escHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ─────────────────────────────────────────────────────────────────────────────
//  EDITORIAL TAB
// ─────────────────────────────────────────────────────────────────────────────

async function sbRenderEditorial(container) {
  container.innerHTML = `<div style="color:var(--text-muted);padding:2rem;">Loading editorial data…</div>`;
  try {
    const data = await apiFetch('/api/blog/editorial');
    container.innerHTML = buildEditorialHtml(data);
  } catch (err) {
    container.innerHTML = `<div style="color:var(--red);padding:1rem;">Failed to load: ${err.message}</div>`;
  }
}

function buildEditorialHtml(data) {
  const { distribution = [], themes = [], gaps = [], recency = [] } = data;

  // Category coverage bars
  const maxPosts = Math.max(...distribution.map(d => d.post_count), 1);
  const coverageBars = distribution.map(d => {
    const pct = Math.round((d.post_count / maxPosts) * 100);
    const color = d.post_count === 0 ? 'var(--red)' : d.days_since_last > 90 ? '#f59e0b' : 'var(--cyan)';
    return `
      <div style="margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
          <span style="font-size:0.8rem;color:var(--text);">${escHtml(d.category)}</span>
          <span style="font-size:0.75rem;color:var(--text-muted);">${d.post_count} post${d.post_count !== 1 ? 's' : ''}${d.days_since_last !== null ? ` · ${d.days_since_last}d ago` : ''}</span>
        </div>
        <div style="height:6px;background:var(--border);border-radius:3px;overflow:hidden;">
          <div style="height:100%;width:${pct}%;background:${color};border-radius:3px;transition:width 0.4s;"></div>
        </div>
      </div>
    `;
  }).join('');

  // Gap severity badges
  const gapColors = { critical: 'var(--red)', high: '#f97316', medium: '#f59e0b', stale: '#a855f7', ok: '#22c55e' };
  const gapRows = gaps.map(g => `
    <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border);">
      <span style="font-size:0.875rem;color:var(--text);">${escHtml(g.label)}</span>
      <div style="display:flex;align-items:center;gap:12px;">
        <span style="font-size:0.75rem;color:var(--text-muted);">${g.post_count} post${g.post_count !== 1 ? 's' : ''}${g.days_since_last ? ` · ${g.days_since_last}d ago` : ''}</span>
        <span style="font-size:0.7rem;font-weight:700;color:${gapColors[g.gap_severity]};border:1px solid ${gapColors[g.gap_severity]};border-radius:4px;padding:1px 7px;text-transform:uppercase;">
          ${g.gap_severity}
        </span>
      </div>
    </div>
  `).join('');

  // Top themes
  const topThemes = themes.slice(0, 20).map(t => {
    const opacity = Math.max(0.4, Math.min(1, t.frequency / (themes[0]?.frequency || 1)));
    return `<span style="display:inline-block;background:var(--cyan)${Math.round(opacity * 22).toString(16).padStart(2,'0')};color:var(--cyan);border-radius:4px;padding:3px 10px;margin:3px;font-size:0.75rem;font-weight:600;">${escHtml(t.theme)} ×${t.frequency}</span>`;
  }).join('');

  // Stale themes (>60 days)
  const staleThemes = recency.filter(r => r.days_ago !== null && r.days_ago > 60).slice(0, 10);
  const staleRows = staleThemes.length > 0
    ? staleThemes.map(r => `
        <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border);">
          <span style="font-size:0.8rem;color:var(--text);">"${escHtml(r.theme)}"</span>
          <span style="font-size:0.75rem;color:#f59e0b;">${r.days_ago}d ago</span>
        </div>
      `).join('')
    : `<div style="color:var(--text-muted);font-size:0.8rem;padding:8px 0;">No stale themes — archive is fresh.</div>`;

  return `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:1.5rem;">

      <!-- Category Coverage -->
      <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;">
          <h3 style="margin:0;font-size:0.9rem;color:var(--text);font-weight:700;text-transform:uppercase;letter-spacing:0.05em;">Category Coverage</h3>
          <div style="display:flex;gap:6px;">
            <button onclick="window.sbImportShopify()"
              style="background:none;border:1px solid var(--cyan);color:var(--cyan);border-radius:5px;padding:3px 10px;font-size:0.72rem;font-weight:600;cursor:pointer;">⬇ Import Shopify</button>
            <button onclick="window.sbRunBackfill('summarize')"
              style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:5px;padding:3px 10px;font-size:0.72rem;cursor:pointer;">Summarize</button>
            <button onclick="window.sbRunBackfill('embed')"
              style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:5px;padding:3px 10px;font-size:0.72rem;cursor:pointer;">Embed</button>
            <button onclick="window.sbRefreshEditorial()"
              style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:5px;padding:3px 10px;font-size:0.75rem;cursor:pointer;">↺</button>
          </div>
        </div>
        ${coverageBars || '<div style="color:var(--text-muted);font-size:0.8rem;">No posts archived yet.</div>'}
      </div>

      <!-- Content Gaps -->
      <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;">
        <h3 style="margin:0 0 1rem;font-size:0.9rem;color:var(--text);font-weight:700;text-transform:uppercase;letter-spacing:0.05em;">Content Gaps</h3>
        ${gapRows || '<div style="color:var(--text-muted);font-size:0.8rem;">No data yet.</div>'}
      </div>

      <!-- Theme Coverage -->
      <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;">
        <h3 style="margin:0 0 1rem;font-size:0.9rem;color:var(--text);font-weight:700;text-transform:uppercase;letter-spacing:0.05em;">Theme Map</h3>
        ${topThemes || '<div style="color:var(--text-muted);font-size:0.8rem;">No themes yet — run backfillBlogSummaries.js first.</div>'}
      </div>

      <!-- Stale Themes -->
      <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;">
        <h3 style="margin:0 0 1rem;font-size:0.9rem;color:var(--text);font-weight:700;text-transform:uppercase;letter-spacing:0.05em;">Stale Territory (60+ days)</h3>
        ${staleRows}
      </div>

    </div>

    <!-- Voice Check -->
    <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;margin-top:1.5rem;">
      <h3 style="margin:0 0 0.75rem;font-size:0.9rem;color:var(--text);font-weight:700;text-transform:uppercase;letter-spacing:0.05em;">Voice Consistency Check</h3>
      <p style="margin:0 0 0.75rem;font-size:0.8rem;color:var(--text-muted);">Paste a draft post's HTML to compare against the archive.</p>
      <textarea id="editorial-vc-input" rows="5" placeholder="Paste draft HTML here…"
        style="width:100%;background:var(--bg);border:1px solid var(--border);color:var(--text);border-radius:6px;
               padding:8px 12px;font-size:0.8rem;resize:vertical;box-sizing:border-box;font-family:monospace;margin-bottom:0.75rem;"></textarea>
      <button onclick="window.sbRunVoiceCheck()"
        style="background:var(--cyan);color:#0d1520;border:none;border-radius:6px;padding:6px 16px;font-weight:700;font-size:0.8rem;cursor:pointer;">
        Check Voice
      </button>
      <div id="editorial-vc-result" style="margin-top:1rem;"></div>
    </div>
  `;
}

window.sbRefreshEditorial = async function() {
  const content = document.getElementById('sb-tab-content');
  if (content) await sbRenderEditorial(content);
};

window.sbImportShopify = async function() {
  if (!confirm('Pull all published Shopify posts into the archive?\n\nThis is safe to run multiple times — existing posts are skipped.')) return;
  try {
    await apiFetch('/api/blog/editorial/import', { method: 'POST' });
    alert('Import started — check the Run Log tab for progress, then refresh Editorial.');
  } catch (err) { alert('Import failed: ' + err.message); }
};

window.sbRunBackfill = async function(mode) {
  const labels = { summarize: 'summarize all posts', embed: 'embed all posts', both: 'summarize + embed all posts' };
  if (!confirm(`This will ${labels[mode] || mode}. Takes several minutes for a full archive.\n\nProceed?`)) return;
  try {
    await apiFetch('/api/blog/editorial/backfill', { method: 'POST', body: JSON.stringify({ mode }) });
    alert(`Backfill (${mode}) started — check the Run Log tab for progress.`);
  } catch (err) { alert('Backfill failed: ' + err.message); }
};

window.sbRunVoiceCheck = async function() {
  const input  = document.getElementById('editorial-vc-input');
  const result = document.getElementById('editorial-vc-result');
  if (!input || !result) return;
  const html = input.value.trim();
  if (!html) { alert('Paste some draft HTML first.'); return; }

  result.innerHTML = '<span style="color:var(--text-muted);font-size:0.8rem;">Checking…</span>';
  try {
    const data = await apiFetch('/api/blog/editorial/voice-check', {
      method: 'POST',
      body: JSON.stringify({ body_html: html }),
    });
    const scoreColor = data.score === null ? 'var(--text-muted)' : data.score >= 0.8 ? '#22c55e' : data.score >= 0.6 ? '#f59e0b' : 'var(--red)';
    const scoreStr   = data.score !== null ? `${Math.round(data.score * 100)}%` : 'N/A';
    const flagHtml   = data.flags?.length > 0
      ? `<ul style="margin:8px 0 0;padding-left:16px;">${data.flags.map(f => `<li style="font-size:0.78rem;color:var(--text-muted);margin-bottom:4px;">${escHtml(f)}</li>`).join('')}</ul>`
      : '<div style="font-size:0.78rem;color:#22c55e;margin-top:6px;">No flags — voice is clean.</div>';

    result.innerHTML = `
      <div style="display:flex;align-items:center;gap:16px;margin-bottom:8px;">
        <span style="font-size:1.5rem;font-weight:800;color:${scoreColor};">${scoreStr}</span>
        <span style="font-size:0.875rem;color:var(--text);">${escHtml(data.verdict || '')}</span>
      </div>
      ${flagHtml}
    `;
  } catch (err) {
    result.innerHTML = `<span style="color:var(--red);font-size:0.8rem;">Check failed: ${escHtml(err.message)}</span>`;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  NEWSLETTER TAB
// ─────────────────────────────────────────────────────────────────────────────

async function sbRenderNewsletter(container) {
  container.innerHTML = `<div style="color:var(--text-muted);padding:2rem;">Loading newsletter…</div>`;
  try {
    const [issues, subscribers] = await Promise.all([
      apiFetch('/api/newsletter').catch(() => []),
      apiFetch('/api/newsletter/subscribers').catch(() => []),
    ]);
    container.innerHTML = buildNewsletterHtml(issues, subscribers);
  } catch (err) {
    container.innerHTML = `<div style="color:var(--red);padding:1rem;">Failed to load newsletter: ${err.message}</div>`;
  }
}

function buildNewsletterHtml(issues, subscribers = []) {
  const statusColor = s => s === 'sent' ? '#2ecc71' : s === 'approved' ? '#00b4d8' : '#e8c547';
  const statusLabel = s => s === 'sent' ? 'Sent' : s === 'approved' ? 'Approved' : 'Draft';

  const issueCards = issues.length === 0
    ? `<p style="color:var(--text-muted);padding:1rem 0;">No newsletter issues yet. Generate your first one below.</p>`
    : issues.map(issue => `
      <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;margin-bottom:1rem;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:0.75rem;">
          <div>
            <span style="font-weight:700;color:var(--text);font-size:0.95rem;">
              Issue #${issue.issue_number} — Arranging Thoughts
            </span>
            <span style="margin-left:10px;font-size:0.75rem;font-weight:700;color:${statusColor(issue.status)};
                         border:1px solid ${statusColor(issue.status)};border-radius:3px;padding:1px 6px;">
              ${statusLabel(issue.status)}
            </span>
          </div>
          <span style="font-size:0.8rem;color:var(--text-muted);">
            ${issue.send_date ? new Date(issue.send_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
          </span>
        </div>
        <p style="margin:0 0 0.5rem;font-size:0.85rem;color:var(--text-muted);">
          <strong style="color:var(--text);">Subject:</strong> ${escHtml(issue.subject_line || '—')}
        </p>
        ${issue.ryan_opener ? `
        <p style="margin:0 0 0.75rem;font-size:0.85rem;color:var(--text-muted);
                  border-left:2px solid var(--cyan);padding-left:10px;font-style:italic;">
          "${escHtml(issue.ryan_opener.substring(0, 120))}${issue.ryan_opener.length > 120 ? '…' : ''}"
        </p>` : ''}
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button onclick="window.sbEditNewsletter(${issue.id})"
            style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:5px;
                   padding:5px 12px;cursor:pointer;font-size:0.8rem;">
            Edit
          </button>
          <button onclick="window.sbPreviewNewsletter(${issue.id})"
            style="background:none;border:1px solid var(--cyan);color:var(--cyan);border-radius:5px;
                   padding:5px 12px;cursor:pointer;font-size:0.8rem;">
            Preview HTML
          </button>
          ${issue.status !== 'sent' ? `
          <button onclick="window.sbApproveNewsletter(${issue.id})"
            style="background:none;border:1px solid #2ecc71;color:#2ecc71;border-radius:5px;
                   padding:5px 12px;cursor:pointer;font-size:0.8rem;">
            ${issue.status === 'approved' ? '✓ Approved' : 'Approve'}
          </button>
          <button onclick="window.sbSendNewsletter(${issue.id})"
            style="background:${issue.status === 'approved' ? 'var(--cyan)' : 'none'};
                   border:1px solid var(--cyan);
                   color:${issue.status === 'approved' ? '#0d1520' : 'var(--text-muted)'};
                   border-radius:5px;padding:5px 12px;cursor:pointer;font-size:0.8rem;font-weight:700;">
            Send
          </button>
          ` : `
          <span style="font-size:0.8rem;color:#2ecc71;">✓ Sent ${issue.sent_at ? relativeTime(new Date(issue.sent_at)) : ''}</span>
          <button onclick="window.sbResendNewsletter(${issue.id})"
            style="background:none;border:1px solid var(--border);color:var(--text-muted);border-radius:5px;
                   padding:5px 12px;cursor:pointer;font-size:0.8rem;">
            Resend
          </button>
          `}
        </div>
      </div>
    `).join('');

  return `
    <div>
      <!-- Generate new issue -->
      <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;margin-bottom:1.5rem;">
        <h3 style="margin:0 0 1rem;font-size:1rem;color:var(--text);">Generate New Issue</h3>
        <p style="margin:0 0 0.75rem;font-size:0.85rem;color:var(--text-muted);">
          Paste Ryan's opening statement. Everything else is generated from the calendar and recent posts.
        </p>
        <textarea id="nl-opener-input" rows="4" placeholder="Type your opener here — 2-4 sentences, whatever's on your mind this week. Runs verbatim at the top."
          style="width:100%;background:var(--bg);border:1px solid var(--border);border-radius:6px;
                 color:var(--text);padding:10px;font-size:0.875rem;resize:vertical;box-sizing:border-box;
                 font-family:inherit;line-height:1.5;margin-bottom:0.75rem;"></textarea>
        <button onclick="window.sbGenerateNewsletter()"
          style="background:var(--cyan);color:#0d1520;border:none;border-radius:6px;
                 padding:8px 20px;font-weight:700;cursor:pointer;font-size:0.875rem;">
          Generate Draft
        </button>
      </div>

      <!-- Issue list -->
      <h3 style="margin:0 0 0.75rem;font-size:1rem;color:var(--text);">All Issues</h3>
      ${issueCards}
    </div>

      <!-- Subscriber list -->
      <h3 style="margin:1.5rem 0 0.75rem;font-size:1rem;color:var(--text);">
        Subscribers
        <span style="font-size:0.8rem;color:var(--text-muted);font-weight:400;margin-left:6px;">
          ${subscribers.filter(s => s.active).length} active
        </span>
      </h3>
      <div style="background:var(--card);border:1px solid var(--border);border-radius:8px;padding:1.25rem;margin-bottom:1.5rem;">
        <table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
          ${subscribers.length === 0 ? `<tr><td style="color:var(--text-muted);padding:0.5rem 0;">No subscribers yet.</td></tr>` :
            subscribers.map(s => `
              <tr style="border-bottom:1px solid var(--border);">
                <td style="padding:8px 0;color:${s.active ? 'var(--text)' : 'var(--text-muted)'};">
                  ${escHtml(s.name || '')}
                </td>
                <td style="padding:8px 0;color:var(--text-muted);">${escHtml(s.email)}</td>
                <td style="padding:8px 0;text-align:right;">
                  <button onclick="window.sbToggleSubscriber(${s.id}, ${!s.active})"
                    style="background:none;border:1px solid var(--border);color:var(--text-muted);
                           border-radius:4px;padding:3px 8px;cursor:pointer;font-size:0.75rem;margin-right:4px;">
                    ${s.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                  <button onclick="window.sbDeleteSubscriber(${s.id})"
                    style="background:none;border:1px solid var(--red);color:var(--red);
                           border-radius:4px;padding:3px 8px;cursor:pointer;font-size:0.75rem;">
                    Remove
                  </button>
                </td>
              </tr>
            `).join('')
          }
        </table>
        <div style="display:flex;gap:8px;margin-top:1rem;align-items:center;flex-wrap:wrap;">
          <input type="text" id="nl-sub-name" placeholder="Name (optional)"
            style="background:var(--bg);border:1px solid var(--border);border-radius:5px;
                   color:var(--text);padding:7px 10px;font-size:0.85rem;width:160px;">
          <input type="email" id="nl-sub-email" placeholder="email@example.com"
            style="background:var(--bg);border:1px solid var(--border);border-radius:5px;
                   color:var(--text);padding:7px 10px;font-size:0.85rem;flex:1;min-width:180px;">
          <button onclick="window.sbAddSubscriber()"
            style="background:var(--cyan);color:#0d1520;border:none;border-radius:5px;
                   padding:7px 14px;font-weight:700;cursor:pointer;font-size:0.85rem;white-space:nowrap;">
            + Add
          </button>
          <button onclick="window.sbImportSubscribers()"
            style="background:none;border:1px solid var(--cyan);color:var(--cyan);border-radius:5px;
                   padding:7px 14px;cursor:pointer;font-size:0.85rem;white-space:nowrap;">
            Import 2026 Season
          </button>
        </div>
      </div>
    </div>

    <!-- Edit modal -->
    <div id="nl-edit-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;
         align-items:center;justify-content:center;padding:1rem;">
      <div style="background:#111d2e;border:1px solid var(--border);border-radius:12px;padding:1.5rem;
                  width:100%;max-width:640px;max-height:90vh;overflow-y:auto;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;">
          <h3 style="margin:0;color:var(--text);">Edit Newsletter Issue</h3>
          <button onclick="window.sbCloseNlModal()"
            style="background:none;border:none;color:var(--text-muted);font-size:1.25rem;cursor:pointer;">✕</button>
        </div>
        <input type="hidden" id="nl-edit-id">
        ${[
          ['subject_line',       'Subject Line',           'text',     1],
          ['ryan_opener',        "Ryan's Opener",          'textarea', 4],
          ['seasonal_paragraph', 'Seasonal Paragraph',     'textarea', 3],
          ['bench_item',         'Piles on My Desk',       'textarea', 2],
          ['postit_note',        'Post-Its on My Monitor', 'textarea', 2],
          ['postit_url',         'Post-It URL',            'text',     1],
          ['cta_line',           'CTA Line',               'text',     1],
          ['send_date',          'Send Date (YYYY-MM-DD)', 'text',     1],
        ].map(([field, label, type, rows]) => `
          <div style="margin-bottom:1rem;">
            <label style="display:block;font-size:0.8rem;color:var(--text-muted);margin-bottom:4px;">${label}</label>
            ${type === 'textarea'
              ? `<textarea id="nl-edit-${field}" rows="${rows}" style="width:100%;background:var(--bg);border:1px solid var(--border);
                   border-radius:5px;color:var(--text);padding:8px;font-size:0.85rem;resize:vertical;
                   box-sizing:border-box;font-family:inherit;"></textarea>`
              : `<input type="text" id="nl-edit-${field}" style="width:100%;background:var(--bg);border:1px solid var(--border);
                   border-radius:5px;color:var(--text);padding:8px;font-size:0.85rem;box-sizing:border-box;">`
            }
          </div>
        `).join('')}
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:1rem;">
          <button onclick="window.sbCloseNlModal()"
            style="background:none;border:1px solid var(--border);color:var(--text-muted);
                   border-radius:6px;padding:8px 16px;cursor:pointer;">Cancel</button>
          <button onclick="window.sbSaveNlEdit()"
            style="background:var(--cyan);color:#0d1520;border:none;
                   border-radius:6px;padding:8px 20px;font-weight:700;cursor:pointer;">Save</button>
        </div>
      </div>
    </div>
  `;
}

window.sbGenerateNewsletter = async function() {
  const opener = document.getElementById('nl-opener-input')?.value?.trim();
  if (!opener) { alert('Paste your opener first.'); return; }
  try {
    await apiFetch('/api/newsletter/generate', { method: 'POST', body: JSON.stringify({ ryan_opener: opener }) });
    alert('Draft generation started — check the Run Log tab for progress, then refresh Newsletter.');
    document.getElementById('nl-opener-input').value = '';
  } catch (err) {
    alert('Error: ' + err.message);
  }
};

window.sbApproveNewsletter = async function(id) {
  try {
    await apiFetch(`/api/newsletter/${id}/approve`, { method: 'POST' });
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderNewsletter(content);
  } catch (err) { alert('Error: ' + err.message); }
};

window.sbSendNewsletter = async function(id) {
  const { rows } = await apiFetch(`/api/newsletter/${id}`).catch(() => ({}));
  const issue = rows || await apiFetch(`/api/newsletter/${id}`);
  if (issue?.status !== 'approved') {
    if (!confirm('This issue is not approved. Approve and send?')) return;
    await apiFetch(`/api/newsletter/${id}/approve`, { method: 'POST' });
  }
  if (!confirm('Send this newsletter to your list via Make.com?')) return;
  try {
    await apiFetch(`/api/newsletter/${id}/send`, { method: 'POST' });
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderNewsletter(content);
  } catch (err) { alert('Send failed: ' + err.message); }
};

window.sbResendNewsletter = async function(id) {
  const email = prompt('Resend to a single address? (leave blank to resend to all subscribers)');
  if (email === null) return; // cancelled
  const target = email.trim() || null;
  const msg = target
    ? `Resend to ${target}?`
    : `Resend this issue to your entire subscriber list?`;
  if (!confirm(msg)) return;
  try {
    const result = await apiFetch(`/api/newsletter/${id}/resend`, {
      method: 'POST',
      body: JSON.stringify(target ? { email: target } : {}),
    });
    alert(`Resent — ${result.sent} sent, ${result.failed} failed.`);
  } catch (err) {
    alert(`Resend failed: ${err.message}`);
  }
};

window.sbPreviewNewsletter = async function(id) {
  try {
    const issue = await apiFetch(`/api/newsletter/${id}`);
    if (!issue.rendered_html) { alert('No rendered HTML yet — save and rerender first.'); return; }
    const win = window.open('', '_blank');
    win.document.write(issue.rendered_html);
    win.document.close();
  } catch (err) { alert('Error: ' + err.message); }
};

window.sbEditNewsletter = async function(id) {
  try {
    const issue = await apiFetch(`/api/newsletter/${id}`);
    document.getElementById('nl-edit-id').value = id;
    const fields = ['subject_line','ryan_opener','seasonal_paragraph','bench_item','postit_note','postit_url','cta_line','send_date'];
    fields.forEach(f => {
      const el = document.getElementById(`nl-edit-${f}`);
      if (el) el.value = issue[f] || '';
    });
    const modal = document.getElementById('nl-edit-modal');
    if (modal) modal.style.display = 'flex';
  } catch (err) { alert('Error: ' + err.message); }
};

window.sbCloseNlModal = function() {
  const modal = document.getElementById('nl-edit-modal');
  if (modal) modal.style.display = 'none';
};

window.sbAddSubscriber = async function() {
  const email = document.getElementById('nl-sub-email')?.value?.trim();
  const name  = document.getElementById('nl-sub-name')?.value?.trim();
  if (!email) { alert('Email is required.'); return; }
  try {
    await apiFetch('/api/newsletter/subscribers', { method: 'POST', body: JSON.stringify({ email, name }) });
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderNewsletter(content);
  } catch (err) { alert('Error: ' + err.message); }
};

window.sbImportSubscribers = async function() {
  if (!confirm('Import all directors and collaborators from 2026 season into the subscriber list?')) return;
  try {
    const result = await apiFetch('/api/newsletter/subscribers/import', {
      method: 'POST',
      body: JSON.stringify({ season_year: 2026 })
    });
    alert(`Import complete: ${result.added} added, ${result.skipped} already existed.`);
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderNewsletter(content);
  } catch (err) { alert('Import failed: ' + err.message); }
};

window.sbToggleSubscriber = async function(id, active) {
  try {
    await apiFetch(`/api/newsletter/subscribers/${id}`, { method: 'PATCH', body: JSON.stringify({ active }) });
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderNewsletter(content);
  } catch (err) { alert('Error: ' + err.message); }
};

window.sbDeleteSubscriber = async function(id) {
  if (!confirm('Remove this subscriber?')) return;
  try {
    await apiFetch(`/api/newsletter/subscribers/${id}`, { method: 'DELETE' });
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderNewsletter(content);
  } catch (err) { alert('Error: ' + err.message); }
};

window.sbSaveNlEdit = async function() {
  const id = document.getElementById('nl-edit-id')?.value;
  if (!id) return;
  const fields = ['subject_line','ryan_opener','seasonal_paragraph','bench_item','postit_note','postit_url','cta_line','send_date'];
  const body = {};
  fields.forEach(f => {
    const val = document.getElementById(`nl-edit-${f}`)?.value;
    if (val !== undefined) body[f] = val || null;
  });
  try {
    await apiFetch(`/api/newsletter/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    await apiFetch(`/api/newsletter/${id}/rerender`, { method: 'POST' });
    window.sbCloseNlModal();
    const content = document.getElementById('sb-tab-content');
    if (content) await sbRenderNewsletter(content);
  } catch (err) { alert('Save failed: ' + err.message); }
};
